import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService, CreateGamePayload, MediaBackfillStatus } from './adminService';
import { productService, ProductError } from '../product/productService';
import { Game, Product } from '../product/types';
import { Button } from '@/components/ui/button';
import { emptyOffers, GameCommerceFields, OfferForm } from './GameCommerceFields';
import { EditionForm, GameEditionsFields } from './GameEditionsFields';

interface GameFormState extends Omit<CreateGamePayload, 'price' | 'purchaseOffers' | 'genres' | 'dateRelease' | 'completionTime' | 'listDLC'> {
  price: string;
  purchaseOffers: OfferForm[];
  genres: string;
  dateRelease: string;
  completionTime: string;
  listDLC: EditionForm[];
}

interface GameFormModalProps {
  formData: GameFormState;
  error: string | null;
  saving: boolean;
  onClose: () => void;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  onPlatformsChange: (platforms: string[]) => void;
  onCommerceChange: (patch: Partial<GameFormState>) => void;
  onMasterFileSelected: (file: File) => void;
  onScreenshotFilesSelected: (files: File[]) => void;
  onRemoveScreenshot: (index: number) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

interface DeleteConfirmationState {
  id: number;
  name: string;
}

const initialFormState: GameFormState = {
  name: '',
  description: '',
  price: '',
  picture: '',
  platforms: [],
  genres: '',
  dateRelease: '',
  completionTime: '',
  online: false,
  multiplayer: false,
  screenshots: [],
  preOrder: false,
  brandNew: false,
  salesLeader: false,
  lowestPrice: false,
  topMetacritic: false,
  showcasePinned: false,
  listDLC: [],
  platformFamily: '',
  store: '',
  purchaseOffers: emptyOffers(),
};

const PLATFORM_OPTIONS = ['PS4', 'PS5', 'Xbox One', 'Xbox Series X/S', 'PC', 'Nintendo Switch', 'Battle.Net', 'Ubisoft', 'Roblox', 'PUBG', 'Minecraft'];

const rubleFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const STORE_LABELS: Record<string, string> = {
  PlayStationStore: 'PlayStation Store', MicrosoftStore: 'Microsoft Store',
  NintendoEshop: 'Nintendo eShop', Steam: 'Steam', Other: 'Другой магазин',
};
const storeLabel = (product: Product) => STORE_LABELS[(product as Game).store] ?? 'Магазин не указан';

const buildPayload = (
  formData: GameFormState
): { payload: CreateGamePayload; error: string | null } => {
  const trimmedName = formData.name.trim();
  const trimmedDescription = formData.description.trim();
  const trimmedPrice = formData.price.trim();
  const parsedPrice = trimmedPrice === '' ? null : Number(trimmedPrice.replace(',', '.'));
  const completionTime = formData.completionTime.trim() === '' ? null : Number(formData.completionTime);
  const purchaseOffers = formData.store === 'PlayStationStore' ? formData.purchaseOffers
    .filter(o => o.price.trim() !== '')
    .map(o => ({ ...o, name: o.fulfillmentType === 'SharedAccount' ? `Шеринг — ${o.activationType === 'Included' ? 'с активацией' : 'без активации'}` : o.region!, description: '', price: Number(o.price.replace(',', '.')), isAvailable: true })) : [];
  const listDLC = formData.listDLC.map(edition => ({
    id: edition.id, name: edition.name.trim(), description: edition.description.trim(), picture: '',
    price: Number(edition.price.replace(',', '.')),
    purchaseOffers: formData.store === 'PlayStationStore' ? edition.purchaseOffers.filter(o => o.price.trim() !== '').map(o => ({ ...o, name: o.fulfillmentType === 'SharedAccount' ? `Шеринг — ${o.activationType === 'Included' ? 'с активацией' : 'без активации'}` : o.region!, description: '', price: Number(o.price.replace(',', '.')), isAvailable: true })) : [],
  }));
  if (purchaseOffers.some(o => Number.isNaN(o.price) || o.price < 0))
    return { payload: { ...formData, genres: [], listDLC: [], purchaseOffers: [], price: 0, completionTime: null }, error: 'Цены предложений должны быть числами не меньше 0.' };
  if (listDLC.some(d => !d.name || Number.isNaN(d.price) || d.price < 0 || d.purchaseOffers.some(o => Number.isNaN(o.price) || o.price < 0)))
    return { payload: { ...formData, genres: [], listDLC: [], purchaseOffers: [], price: 0, completionTime: null }, error: 'У каждого издания должны быть название и корректные цены.' };
  if (completionTime !== null && (!Number.isInteger(completionTime) || completionTime < 0))
    return { payload: { ...formData, genres: [], listDLC: [], purchaseOffers: [], price: 0, completionTime: null }, error: 'Время прохождения должно быть целым числом не меньше 0.' };

  if (!trimmedName) {
    return {
      payload: { ...formData, genres: [], listDLC: [], purchaseOffers: [], name: '', description: trimmedDescription, picture: '', screenshots: [], price: 0, completionTime: null },
      error: 'Заполни название игры.',
    };
  }

  if (parsedPrice !== null && (Number.isNaN(parsedPrice) || parsedPrice < 0)) {
    return {
      payload: { ...formData, genres: [], listDLC: [], purchaseOffers: [], name: trimmedName, description: trimmedDescription, picture: '', screenshots: [], price: 0, completionTime: null },
      error: 'Цена должна быть числом не меньше 0.',
    };
  }

  // picture/screenshots грузятся отдельно после создания товара, в payload их не шлём
  return {
    payload: {
      ...formData,
      name: trimmedName,
      description: trimmedDescription,
      picture: '',
      screenshots: [],
      price: parsedPrice ?? 0,
      purchaseOffers,
      genres: formData.genres.split(',').map(g => g.trim()).filter(Boolean),
      dateRelease: formData.dateRelease || null,
      completionTime,
      listDLC,
    },
    error: null,
  };
};

const GameFormModal = ({
  formData,
  error,
  saving,
  onClose,
  onChange,
  onPlatformsChange,
  onCommerceChange,
  onMasterFileSelected,
  onScreenshotFilesSelected,
  onRemoveScreenshot,
  onSubmit,
}: GameFormModalProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const screenshotsInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onMasterFileSelected(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleScreenshotsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) onScreenshotFilesSelected(files);
    if (screenshotsInputRef.current) screenshotsInputRef.current.value = '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/50 px-4 py-6">
      <div className="my-auto w-full max-w-4xl rounded-3xl bg-white p-6 text-gray-900 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Новая игра</h2>
            <p className="mt-1 text-sm text-gray-500">
              Заполни карточку и сохрани игру в каталог.
            </p>
          </div>

          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            size="icon"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:bg-gray-100"
            aria-label="Закрыть форму"
          >
            ×
          </Button>
        </div>

        <form className="mt-6 space-y-5" onSubmit={onSubmit}>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">Название</span>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={onChange}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
                placeholder="Например, EA Sports FC 26"
              />
            </label>

            <GameCommerceFields family={formData.platformFamily} store={formData.store} offers={formData.purchaseOffers}
              onFamilyChange={platformFamily => onCommerceChange({ platformFamily })}
              onStoreChange={store => onCommerceChange(store === 'PlayStationStore' ? { store, platformFamily: 'PlayStation' } : { store })}
              onOffersChange={purchaseOffers => onCommerceChange({ purchaseOffers })} />

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">Базовая цена</span>
              <input
                type="text"
                name="price"
                inputMode="decimal"
                value={formData.price}
                onChange={onChange}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
                placeholder="Например, 1999"
              />
              <span className="mt-1 block text-xs text-gray-400">Для PlayStation Store будет заменена минимальной ценой из предложений.</span>
            </label>

            <div className="block sm:col-span-2">
              <span className="mb-2 block text-sm font-medium text-gray-700">Платформы</span>
              <div className="flex flex-wrap gap-2">
                {PLATFORM_OPTIONS.map((p) => (
                  <label key={p} className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-gray-300"
                      checked={formData.platforms.includes(p)}
                      onChange={(e) =>
                        onPlatformsChange(
                          e.target.checked
                            ? [...formData.platforms, p]
                            : formData.platforms.filter((x) => x !== p)
                        )
                      }
                    />
                    {p}
                  </label>
                ))}
              </div>
            </div>

            <label className="block sm:col-span-2">
              <span className="mb-2 block text-sm font-medium text-gray-700">Жанры</span>
              <input name="genres" value={formData.genres} onChange={onChange} placeholder="Экшен, RPG, Приключения" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white" />
              <span className="mt-1 block text-xs text-gray-400">Укажи через запятую</span>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">Дата выхода</span>
              <input type="date" name="dateRelease" value={formData.dateRelease} onChange={onChange} className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900" />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">Время прохождения, ч.</span>
              <input type="number" min="0" step="1" name="completionTime" value={formData.completionTime} onChange={onChange} placeholder="Например, 25" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900" />
            </label>

            <div className="block sm:col-span-2">
              <span className="mb-2 block text-sm font-medium text-gray-700">Изображение</span>

              <div
                onClick={() => fileInputRef.current?.click()}
                className="relative mb-2 flex h-36 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 transition hover:border-gray-400 hover:bg-gray-100"
              >
                {formData.picture ? (
                  <img src={formData.picture} alt="preview" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-gray-400">
                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2 1.586-1.586a2 2 0 0 1 2.828 0L20 14M14 8h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" />
                    </svg>
                    <span className="text-xs">Нажмите для загрузки</span>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Описание</span>
            <textarea
              name="description"
              value={formData.description}
              onChange={onChange}
              rows={4}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
              placeholder="Кратко опиши игру (необязательно)"
            />
          </label>

          <GameEditionsFields editions={formData.listDLC} playStationStore={formData.store === 'PlayStationStore'} onChange={listDLC => onCommerceChange({ listDLC })} />

          {/* Скриншоты */}
          <div>
            <span className="mb-2 block text-sm font-medium text-gray-700">Скриншоты</span>

            {formData.screenshots.length > 0 && (
              <div className="mb-3 grid grid-cols-3 gap-2">
                {formData.screenshots.map((url, i) => (
                  <div key={i} className="group relative aspect-video overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
                    <img src={url} alt={`screenshot-${i}`} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => onRemoveScreenshot(i)}
                      className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition hover:bg-black/80 group-hover:opacity-100"
                    >
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => screenshotsInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 py-3 text-sm text-gray-400 transition hover:border-gray-400 hover:text-gray-600"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
              </svg>
              Добавить скриншоты
            </button>
            <input
              ref={screenshotsInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleScreenshotsChange}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input type="checkbox" name="online" checked={formData.online} onChange={onChange} className="h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-gray-700">Онлайн</span>
            </label>
            <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input type="checkbox" name="multiplayer" checked={formData.multiplayer} onChange={onChange} className="h-4 w-4 rounded border-gray-300" />
              <span className="text-sm text-gray-700">Вдвоём на одном экране</span>
            </label>
            <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                name="preOrder"
                checked={formData.preOrder}
                onChange={onChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Предзаказ</span>
            </label>

            <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                name="brandNew"
                checked={formData.brandNew}
                onChange={onChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Новинка</span>
            </label>

            <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                name="salesLeader"
                checked={formData.salesLeader}
                onChange={onChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Лидер продаж</span>
            </label>

<label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                name="lowestPrice"
                checked={formData.lowestPrice}
                onChange={onChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Дешевле не было</span>
            </label>

<label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                name="topMetacritic"
                checked={formData.topMetacritic}
                onChange={onChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Лучшие по Metacritic</span>
            </label>

<label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                name="showcasePinned"
                checked={formData.showcasePinned}
                onChange={onChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Не менять витринные флаги при синхронизации Reloc</span>
            </label>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
            >
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? 'Сохранение...' : 'Сохранить игру'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteConfirmationModal = ({
  gameName,
  deleting,
  error,
  onCancel,
  onConfirm,
}: {
  gameName: string;
  deleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4 py-6">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <h2 className="text-xl font-bold text-gray-900">Удалить игру?</h2>
        <p className="mt-2 text-sm text-gray-500">
          Игра <span className="font-semibold text-gray-900">{gameName}</span> будет удалена
          без возможности восстановления.
        </p>

        {error && (
          <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            variant="outline"
            className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Отмена
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            variant="destructive"
            className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
          >
            {deleting ? 'Удаление...' : 'Подтвердить удаление'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 20;

const AdminProductsPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formData, setFormData] = useState<GameFormState>(initialFormState);
  // Файлы картинок держим до создания товара, затем грузим их к его id
  const [masterFile, setMasterFile] = useState<File | null>(null);
  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);
  const [mediaStatus, setMediaStatus] = useState<MediaBackfillStatus | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchRef = useRef(search);
  searchRef.current = search;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const load = async (pageNum: number, query: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await productService.fetchProducts({
        name: query.trim() || undefined,
        pageNumber: pageNum,
        pageSize: PAGE_SIZE,
      });
      setProducts(res.products);
      setTotal(res.total);
    } catch (err) {
      setError((err as ProductError).message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const status = await adminService.getMediaBackfillStatus();
        if (active) setMediaStatus(status);
      } catch {
        if (active) setMediaError('Не удалось получить статус оптимизации медиа.');
      }
    };
    refresh();
    const timer = window.setInterval(refresh, 2000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  const startMediaBackfill = async () => {
    try {
      setMediaError(null);
      setMediaStatus(await adminService.startMediaBackfill());
    } catch (err: any) {
      const current = err?.response?.data as MediaBackfillStatus | undefined;
      if (current?.state) setMediaStatus(current);
      else setMediaError('Не удалось запустить оптимизацию медиа.');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      load(1, value);
    }, 350);
  };

  const filteredProducts = products;

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (event.target as HTMLInputElement).checked
          : value,
    }));
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setMasterFile(null);
    setScreenshotFiles([]);
  };

  const closeCreateModal = () => {
    if (isSaving) return;
    setIsCreateModalOpen(false);
    setSaveError(null);
    resetForm();
  };

  const handleMasterFileSelected = (file: File) => {
    setMasterFile(file);
    setFormData((prev) => ({ ...prev, picture: URL.createObjectURL(file) }));
  };

  const handleScreenshotFilesSelected = (files: File[]) => {
    setScreenshotFiles((prev) => [...prev, ...files]);
    setFormData((prev) => ({
      ...prev,
      screenshots: [...prev.screenshots, ...files.map((f) => URL.createObjectURL(f))],
    }));
  };

  const handleRemoveScreenshot = (index: number) => {
    setScreenshotFiles((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index),
    }));
  };

  const handleCreateGame = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const { payload, error: payloadError } = buildPayload(formData);
    if (payloadError) {
      setSaveError(payloadError);
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);

      const createdGame = await adminService.createGame(payload);

      // Картинки грузим уже к созданному товару
      if (masterFile) {
        await adminService.uploadMasterImage(createdGame.id, masterFile);
      }
      for (const file of screenshotFiles) {
        await adminService.uploadScreenshotImage(createdGame.id, file);
      }

      setIsCreateModalOpen(false);
      resetForm();
      load(page, searchRef.current);
    } catch (err) {
      setSaveError((err as ProductError).message || 'Не удалось сохранить игру.');
    } finally {
      setIsSaving(false);
    }
  };

  const requestDeleteGame = (id: number) => {
    const game = products.find((product) => product.id === id);
    setDeleteError(null);
    setDeleteConfirmation({
      id,
      name: game?.name || `#${id}`,
    });
  };

  const handleDeleteGame = async () => {
    if (!deleteConfirmation) return;

    try {
      setDeletingId(deleteConfirmation.id);
      setDeleteError(null);
      await adminService.deleteGame(deleteConfirmation.id);
      setProducts((prev) =>
        prev.filter((product) => product.id !== deleteConfirmation.id)
      );
      setDeleteConfirmation(null);
    } catch (err) {
      const deleteError = err as ProductError;
      setDeleteError(deleteError.message || 'Не удалось удалить игру.');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 bg-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="h-8 w-56 animate-pulse rounded bg-gray-200" />
            <div className="mt-2 h-4 w-64 max-w-full animate-pulse rounded bg-gray-100" />
          </div>
          <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200 sm:w-32" />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
              <div className="mt-4 h-8 w-20 animate-pulse rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl bg-white">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-red-700">
            Не удалось загрузить товары
          </h2>
          <p className="mt-2 text-sm text-red-600">{error}</p>
          <Button
            onClick={() => window.location.reload()}
            variant="destructive"
            className="mt-4 inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium"
          >
            Попробовать снова
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-white">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Товары</h1>
          <p className="mt-1 text-sm text-gray-500">
            Всего: {total}
          </p>
        </div>

        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex w-full items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 sm:w-auto"
        >
          + Добавить товар
        </Button>
      </div>

      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Оптимизация старых изображений</h2>
            <p className="mt-1 text-sm text-gray-600">
              Сервер создаст WebP 320/640/960 px. Оригиналы останутся без изменений.
            </p>
            {mediaStatus && mediaStatus.state !== 'idle' && (
              <p className="mt-2 text-sm font-medium text-gray-700">
                {mediaStatus.state === 'queued' && 'Задача ожидает запуска…'}
                {mediaStatus.state === 'running' && `Обработано ${mediaStatus.processed} из ${mediaStatus.total} · успешно ${mediaStatus.succeeded} · ошибок ${mediaStatus.failed}`}
                {mediaStatus.state === 'completed' && `Готово: оптимизировано ${mediaStatus.succeeded} изображений.`}
                {mediaStatus.state === 'completed_with_errors' && `Завершено с ошибками: осталось ${mediaStatus.remaining}. Можно запустить повторно.`}
                {mediaStatus.state === 'failed' && 'Задача остановилась из-за ошибки.'}
                {mediaStatus.state === 'cancelled' && 'Задача была остановлена.'}
              </p>
            )}
            {mediaError && <p className="mt-2 text-sm text-red-600">{mediaError}</p>}
            {!!mediaStatus?.failures?.length && mediaStatus.state !== 'running' && (
              <p className="mt-1 text-xs text-red-600">
                Последняя ошибка: #{mediaStatus.failures[mediaStatus.failures.length - 1].id} — {mediaStatus.failures[mediaStatus.failures.length - 1].error}
              </p>
            )}
          </div>
          <Button
            type="button"
            onClick={startMediaBackfill}
            disabled={mediaStatus?.state === 'queued' || mediaStatus?.state === 'running'}
            className="shrink-0 rounded-xl bg-blue-700 px-4 py-2.5 text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mediaStatus?.state === 'queued' || mediaStatus?.state === 'running'
              ? 'Оптимизация идёт…'
              : mediaStatus?.remaining
                ? 'Повторить оптимизацию'
                : 'Оптимизировать медиа'}
          </Button>
        </div>
        {mediaStatus?.state === 'running' && mediaStatus.total > 0 && (
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-blue-100">
            <div
              className="h-full rounded-full bg-blue-600 transition-all"
              style={{ width: `${Math.min(100, (mediaStatus.processed / mediaStatus.total) * 100)}%` }}
            />
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Список товаров</h2>
            <p className="text-sm text-gray-500">
              {total > 0 ? `Страница ${page} из ${totalPages} · ${total} товаров` : 'Нет товаров'}
            </p>
          </div>

          <div className="w-full md:w-80">
            <input
              type="text"
              placeholder="Поиск по названию..."
              value={search}
              onChange={handleSearchChange}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-gray-400 focus:bg-white"
            />
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-2xl">
              📦
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Ничего не найдено</h3>
            <p className="mt-2 max-w-md text-sm text-gray-500">
              Попробуй изменить поисковый запрос или добавь новый товар в каталог.
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 p-4 sm:p-5 lg:hidden">
              {filteredProducts.map((p) => (
                <div
                  key={p.id}
                  className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white text-sm font-semibold text-gray-600 shadow-sm">
                      {p.name.charAt(0).toUpperCase()}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">{p.name}</p>
                        <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-200">
                          #{p.id}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">{storeLabel(p)}{(p as Game).platforms?.length ? ` · ${(p as Game).platforms.join(', ')}` : ''}</p>
                      <p className="mt-4 text-lg font-semibold text-gray-900">
                        {rubleFormatter.format(p.price)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <Link
                      to={`/admin/products/${p.id}/edit`}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-center text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                    >
                      Редактировать
                    </Link>
                    <Button
                      onClick={() => requestDeleteGame(p.id)}
                      disabled={deletingId === p.id}
                      variant="outline"
                      className="rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {deletingId === p.id ? 'Удаление...' : 'Удалить'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden overflow-x-auto lg:block">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Название
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Цена
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Действия
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {filteredProducts.map((p) => (
                    <tr key={p.id} className="transition hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-700">#{p.id}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-sm font-semibold text-gray-600">
                            {p.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{p.name}</p>
                            <p className="text-xs text-gray-500">{storeLabel(p)}{(p as Game).platforms?.length ? ` · ${(p as Game).platforms.join(', ')}` : ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {rubleFormatter.format(p.price)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/admin/products/${p.id}/edit`}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                          >
                            Редактировать
                          </Link>
                          <Button
                            onClick={() => requestDeleteGame(p.id)}
                            disabled={deletingId === p.id}
                            variant="outline"
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {deletingId === p.id ? 'Удаление...' : 'Удалить'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
                <p className="text-sm text-gray-500">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} из {total}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                    variant="outline"
                    className="h-9 w-9 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-40"
                  >
                    ‹
                  </Button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                    .reduce<(number | '...')[]>((acc, n, i, arr) => {
                      if (i > 0 && (n as number) - (arr[i - 1] as number) > 1) acc.push('...');
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((item, i) =>
                      item === '...' ? (
                        <span key={`ellipsis-${i}`} className="px-1 text-sm text-gray-400">…</span>
                      ) : (
                        <Button
                          key={item}
                          type="button"
                          onClick={() => setPage(item as number)}
                          variant="outline"
                          className={`h-9 w-9 rounded-lg border p-0 text-sm ${
                            page === item
                              ? 'border-gray-900 bg-gray-900 text-white'
                              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {item}
                        </Button>
                      )
                    )}

                  <Button
                    type="button"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page === totalPages}
                    variant="outline"
                    className="h-9 w-9 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-40"
                  >
                    ›
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {isCreateModalOpen && (
        <GameFormModal
          formData={formData}
          error={saveError}
          saving={isSaving}
          onClose={closeCreateModal}
          onChange={handleFieldChange}
          onPlatformsChange={(platforms) => setFormData((prev) => ({ ...prev, platforms }))}
          onCommerceChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
          onMasterFileSelected={handleMasterFileSelected}
          onScreenshotFilesSelected={handleScreenshotFilesSelected}
          onRemoveScreenshot={handleRemoveScreenshot}
          onSubmit={handleCreateGame}
        />
      )}

      {deleteConfirmation && (
        <DeleteConfirmationModal
          gameName={deleteConfirmation.name}
          deleting={deletingId === deleteConfirmation.id}
          error={deleteError}
          onCancel={() => {
            if (deletingId !== deleteConfirmation.id) {
              setDeleteConfirmation(null);
              setDeleteError(null);
            }
          }}
          onConfirm={handleDeleteGame}
        />
      )}
    </div>
  );
};

export default AdminProductsPage;
